import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import { socketClient } from '../index';

const emitScopedStatsRequest = ({
  emitEvent,
  successEvent,
  errorEvent,
  payload,
  timeoutMessage
}) =>
  new Promise((resolve, reject) => {
    const handleSuccess = (response) => {
      if (response?.requestId !== payload.requestId) {
        return;
      }

      clearTimeout(timeoutId);
      socketClient.off(successEvent, handleSuccess);
      socketClient.off(errorEvent, handleError);
      resolve(response);
    };

    const handleError = (response) => {
      if (response?.requestId !== payload.requestId) {
        return;
      }

      clearTimeout(timeoutId);
      socketClient.off(successEvent, handleSuccess);
      socketClient.off(errorEvent, handleError);
      reject(new Error(response?.message || timeoutMessage));
    };

    const timeoutId = setTimeout(() => {
      socketClient.off(successEvent, handleSuccess);
      socketClient.off(errorEvent, handleError);
      reject(new Error(timeoutMessage));
    }, 15000);

    socketClient.on(successEvent, handleSuccess);
    socketClient.on(errorEvent, handleError);
    socketClient.emit(emitEvent, payload);
  });

const initialState = {
  stats: {},
  labourStats: {},
  techChartData: [],
  monthlyChartData: {},
  customerAquisition: {},
  yearlyChartData: {},
  monthlyWebsiteChartData: {},
  visitsByCityData: {},
  channelVisits: {},
  pageVisits: [],
  aiResponse: '',
  loading: false,
  statsLoading: false,
  yearlyChartLoading: false,
  customerAquisitionLoading: false,
  error: null,
  show: false
};

let aiAssistantListener = null;
let aiStreamDoneListener = null;

export const requestGetStats = createAsyncThunk(
  'stats/requestGetStats',
  async function (_, { rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const requestId = `stats:summary:${Date.now()}:${Math.random().toString(36).slice(2)}`;

      return await emitScopedStatsRequest({
        emitEvent: 'getStats',
        successEvent: 'getStatsSuccess',
        errorEvent: 'getStatsError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading dashboard statistics'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load dashboard statistics');
    }
  }
);

export const requestGetYearlyChartData = createAsyncThunk(
  'stats/requestGetYearlyChartData',
  async function (_, { rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const requestId = `stats:yearly:${Date.now()}:${Math.random().toString(36).slice(2)}`;

      return await emitScopedStatsRequest({
        emitEvent: 'getYearlyChartData',
        successEvent: 'getYearlyChartDataSuccess',
        errorEvent: 'getYearlyChartDataError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading yearly chart data'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load yearly chart data');
    }
  }
);

export const requestGetCustomerAquisition = createAsyncThunk(
  'stats/requestGetCustomerAquisition',
  async function (_, { rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const requestId = `stats:customer-acquisition:${Date.now()}:${Math.random().toString(36).slice(2)}`;

      return await emitScopedStatsRequest({
        emitEvent: 'getCustomerAquisition',
        successEvent: 'getCustomerAquisitionSuccess',
        errorEvent: 'getCustomerAquisitionError',
        payload: { idToken, requestId },
        timeoutMessage: 'Timed out while loading customer acquisition data'
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load customer acquisition data');
    }
  }
);

export const fetchAiAssistant = createAsyncThunk(
  'stats/fetchAiAssistant',
  async function (_, { dispatch }) {
    if (aiAssistantListener) {
      return;
    }

    aiAssistantListener = (data) => {
      dispatch(setAiResponse(data));
      dispatch(setShow(true));
    };

    socketClient.on('askAiAssistant', aiAssistantListener);
  }
);

export const fetchStreamDone = createAsyncThunk(
  'stats/fetchStreamDone',
  async function (_, { dispatch }) {
    if (aiStreamDoneListener) {
      return;
    }

    aiStreamDoneListener = () => {
      dispatch(setAiResponse(''));
    };

    socketClient.on('streamDone', aiStreamDoneListener);
  }
);

const slice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    setAiResponse(state, action) {
      state.aiResponse = action.payload;
    },
    setShow(state, action) {
      state.show = action.payload;
    },
    clearStatsError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestGetStats.pending, (state) => {
        state.statsLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(requestGetStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.loading = state.yearlyChartLoading || state.customerAquisitionLoading;
        state.error = null;
        state.stats = action.payload?.stats || {};
        state.pageVisits = action.payload?.stats?.googlePageTitleStats || [];
        state.visitsByCityData = action.payload?.stats?.googleVisitsByMonth || {};
        state.channelVisits = action.payload?.stats?.googleVisitsByChannelGroup || {};
        state.monthlyWebsiteChartData = action.payload?.stats?.websiteAnalytics || {};
        state.monthlyChartData = action.payload?.stats?.monthlyChartData || {};
        state.techChartData = action.payload?.statsTech || [];
      })
      .addCase(requestGetStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.loading = state.yearlyChartLoading || state.customerAquisitionLoading;
        state.error = action.payload || action.error?.message || 'Unable to load dashboard statistics';
      })
      .addCase(requestGetYearlyChartData.pending, (state) => {
        state.yearlyChartLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(requestGetYearlyChartData.fulfilled, (state, action) => {
        state.yearlyChartLoading = false;
        state.loading = state.statsLoading || state.customerAquisitionLoading;
        state.error = null;
        state.yearlyChartData = action.payload || {};
        state.labourStats = action.payload?.techLabour || {};
      })
      .addCase(requestGetYearlyChartData.rejected, (state, action) => {
        state.yearlyChartLoading = false;
        state.loading = state.statsLoading || state.customerAquisitionLoading;
        state.error = action.payload || action.error?.message || 'Unable to load yearly chart data';
      })
      .addCase(requestGetCustomerAquisition.pending, (state) => {
        state.customerAquisitionLoading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(requestGetCustomerAquisition.fulfilled, (state, action) => {
        state.customerAquisitionLoading = false;
        state.loading = state.statsLoading || state.yearlyChartLoading;
        state.error = null;
        state.customerAquisition = action.payload || {};
      })
      .addCase(requestGetCustomerAquisition.rejected, (state, action) => {
        state.customerAquisitionLoading = false;
        state.loading = state.statsLoading || state.yearlyChartLoading;
        state.error = action.payload || action.error?.message || 'Unable to load customer acquisition data';
      })
      .addCase(fetchAiAssistant.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAiAssistant.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAiAssistant.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchStreamDone.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStreamDone.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchStreamDone.rejected, (state) => {
        state.loading = false;
      });
  }
});

export const { setAiResponse, setShow, clearStatsError } = slice.actions;

export const reducer = slice.reducer;
